import React from "react";
import { Text } from "@salestrak/ui";
import { AGE_STATUS_CONFIG, type AgeStatus } from "#app/lib/age-status";
import { formatUpdateCycle } from "#app/lib/project-updates";
import type { ActivityChange } from "../activity-types";

interface AgeChangeChunkProps {
  change: ActivityChange;
}

/** The status word a stored age status reads by, or null for none. */
function statusWord(status: unknown): string | null {
  if (typeof status !== "string") return null;
  return AGE_STATUS_CONFIG[status as AgeStatus]?.label ?? null;
}

/**
 * A Follow up clock change that is not a posted Update. `triggeredBy` says
 * what moved the clock, and each cause reads as what happened: a cycle edit
 * is the one the actor made, while a Stage entry and an expiry are the clock
 * moving on its own — never "changed to On track" as if someone posted.
 */
export function AgeChangeChunk({ change }: AgeChangeChunkProps) {
  const before = statusWord(change.oldValue?.status);
  const after = statusWord(change.newValue?.status);

  switch (change.subject) {
    case "cycleChanged": {
      const cycle = change.newValue?.cycle;
      return (
        <Text size="sm">
          {cycle
            ? `changed the follow-up cycle to ${formatUpdateCycle(cycle).toLowerCase()}`
            : "reset the follow-up cycle to the stage default"}
        </Text>
      );
    }
    case "stageChanged":
      if (!after) return <Text size="sm">ended the follow up</Text>;
      return (
        <Text size="sm">
          {before ? "restarted" : "started"} the follow up on entering the stage
        </Text>
      );
    default:
      return (
        <Text size="sm">
          {after ? `changed follow up to ${after}` : "changed follow up"}
        </Text>
      );
  }
}
